<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/api';
import { PRODUCT_NAME } from '@/constants';
import { DgrSelectbox, SelectboxItem } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import MsTeamsSettings from '@/components-capability/admin/environment-settings/ms-teams-settings.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Content from '@/components/layouts/content.vue';
import Header from '@/components/layouts/header.vue';
import {
  DeliveryAt,
  MailDeliverySchedule,
  MSTeamsSettingsWithType,
  ScheduledNotificationType,
} from '@/types';
import {
  STATES,
  useMailDeliverySchedules,
  useMSTeamsSettings,
  useTeamInfo,
} from '@/utils/swr';
import { checkMoveToAdmin } from '@/utils/user';

type MailDeliveryDisplayItem = {
  scheduledNotificationType: ScheduledNotificationType;
  name: string;
  options: SelectboxItem<DeliveryAt>[];
  selectedValue: DeliveryAt;
};

const router = useRouter();
const { createSnackbar } = useSnackbar();

const settings = ref<MSTeamsSettingsWithType | undefined>(undefined);

const { data: teamInfo } = useTeamInfo();

const { data: mailDeliverySchedules, state: mailDeliveryScheduleState } =
  useMailDeliverySchedules();

const enableTechnicalLiterature = computed(
  () => teamInfo.value?.enable_technical_literature,
);

// メール配信設定の表示および、選択した時刻の保存に使用する
const mailDeliveryDisplayItems = ref<MailDeliveryDisplayItem[]>([
  {
    scheduledNotificationType: 'favorite_theme_mail',
    name: enableTechnicalLiterature.value
      ? '新着ニュース・新着論文・特許'
      : '新着ニュース',
    options: [
      {
        label: '7時〜8時',
        value: '07:00',
      },
      {
        label: '8時〜9時',
        value: '08:00',
      },
      {
        label: '9時〜10時',
        value: '09:00',
      },
      {
        label: '10時〜11時',
        value: '10:00',
      },
    ],
    selectedValue: '08:00',
  },
  {
    scheduledNotificationType: 'team_activity_mail',
    name: '先週のハイライト',
    options: [
      {
        label: '7時〜8時',
        value: '07:00',
      },
      {
        label: '8時〜9時',
        value: '08:00',
      },
      {
        label: '9時〜10時',
        value: '09:00',
      },
      {
        label: '10時〜11時',
        value: '10:00',
      },
    ],
    selectedValue: '08:00',
  },
  {
    scheduledNotificationType: 'overlooked_mail',
    name: '本日の読み逃しニュース',
    options: [
      {
        label: '16時〜17時',
        value: '16:00',
      },
      {
        label: '17時〜18時',
        value: '17:00',
      },
      {
        label: '18時〜19時',
        value: '18:00',
      },
    ],
    selectedValue: '17:00',
  },
]);
watch(mailDeliverySchedules, () => {
  if (!mailDeliverySchedules.value) return;

  for (const item of mailDeliveryDisplayItems.value) {
    const schedule = mailDeliverySchedules.value.mail_delivery_schedules.find(
      x => x.scheduled_notification_type === item.scheduledNotificationType,
    )?.deliver_at;

    if (schedule) {
      item.selectedValue = schedule;
    }
  }
});

onMounted(async () => {
  await checkMoveToAdmin(router);
});

const {
  data: teamsSettings,
  error: teamsSettingsError,
  mutate: mutateTeamsSettings,
} = useMSTeamsSettings();
watch(
  [teamsSettings, teamsSettingsError],
  ([data, error]) => {
    if (data) {
      settings.value = { settings: data, submittable: true };
      return;
    }
    // Teams連携を一度も設定していないチームは404が返る
    if (isAxiosError(error) && error.response?.status === 404) {
      settings.value = {
        settings: {
          webhook_url: '',
          enabled_ms_teams_notification: false,
          enabled_team_activity_content: false,
          enabled_mark_content: false,
        },
        submittable: true,
      };
    }
  },
  { immediate: true },
);

const submit = async () => {
  const submitSchedules: MailDeliverySchedule[] =
    mailDeliveryDisplayItems.value.map(item => ({
      scheduled_notification_type: item.scheduledNotificationType,
      deliver_at: item.selectedValue,
    }));

  if (enableTechnicalLiterature.value) {
    // 画面上は同じ設定を使っているので、新着論文の配信時刻は新着ニュースの配信時刻と同じにする。
    const newResearchPaperMailSchedule: MailDeliverySchedule = {
      scheduled_notification_type: 'new_technical_literature_mail',
      deliver_at:
        mailDeliveryDisplayItems.value.find(
          item => item.scheduledNotificationType === 'favorite_theme_mail',
        )?.selectedValue ?? '08:00', // 08:00 はデフォルト値
    };
    submitSchedules.push(newResearchPaperMailSchedule);
  }

  if (!settings.value?.submittable) return;
  try {
    if (teamInfo.value?.enable_ms_teams_integration) {
      const savedSettings = { ...settings.value.settings };
      await api.updateMSTeamsSettings(savedSettings);
      // 取り直しに失敗してメール配信の保存が止まらないよう、保存した値でキャッシュだけを更新する
      await mutateTeamsSettings(() => savedSettings);
    }
    await api.updateMailDeliverySchedules(submitSchedules);
    createSnackbar({
      message: '環境設定を更新しました',
      type: 'success',
    });
  } catch {
    createSnackbar({
      message: '環境設定を更新できませんでした',
      type: 'error',
    });
  }
};
</script>

<template>
  <div class="o-service-integrations">
    <Header title="環境設定"></Header>
    <Content>
      <div class="environment-settings-body">
        <div
          class="o-integration"
          v-if="teamInfo && teamInfo.enable_ms_teams_integration"
        >
          <div class="teams-integration">
            <MsTeamsSettings
              class="environment-settings-content teams"
              :title="`${PRODUCT_NAME}全体の情報をTeamsに配信`"
              v-model="settings"
            >
              Microsoft Teamsのチャネルに{{
                PRODUCT_NAME
              }}の情報を配信します。<br />グループ毎に設定する場合は、
              <a
                class="doc-link"
                href="https://anews-stockmark.zendesk.com/hc/ja/articles/4402116895897"
                target="_blank"
                rel="noopener noreferrer"
                >こちら</a
              >
              をご覧ください。
            </MsTeamsSettings>
          </div>
        </div>
        <div class="mail-delivery">
          <div class="environment-setting-category-title c-title-bold--s">
            定期配信メール設定
          </div>
          <div class="spacing-12"></div>
          <div class="c-text-regular--m">
            メールの配信時間設定を行うことができます。
          </div>
          <div class="spacing-12"></div>
          <div
            class="mail-delivery-content-wap"
            v-if="mailDeliveryScheduleState === STATES.SUCCESS"
          >
            <div
              class="mail-delivery-content environment-settings-content"
              v-for="item in mailDeliveryDisplayItems"
              :key="item.scheduledNotificationType"
            >
              <div class="mail-delivery-item">
                <div class="c-formBlock__label">{{ item.name }}</div>
                <DgrSelectbox
                  class="mail-delivery-selectbox"
                  :options="item.options"
                  v-model="item.selectedValue"
                  size="small"
                ></DgrSelectbox>
                <div class="spacing-12"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Content>
    <div class="settings-footer">
      <button
        class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
        :disabled="!settings?.submittable"
        :class="{ disabled: !settings?.submittable }"
        @click.stop="submit"
      >
        保存
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// 内容が短くても画面の最下部に置く
.settings-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 24px;
  background: $color-white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
}

.o-service-integrations {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  // コンテンツを内容領域いっぱいに広げる（Header・Contentの幅もこの変数で決まる）
  --layout-content-width: 100%;
}
.environment-settings-body {
  max-width: 1248px;
  margin: 0 24px 24px;
  background-color: white;
  border: 1px solid $color-gray400;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 16px;

  display: flex;
  flex-direction: column;
  gap: 24px;

  .environment-setting-category-title {
    color: $color-gray600;
  }

  // Teams連携は子コンポーネント（ms-teams-settings.vue）のため、この画面からだけ間隔を上書きする
  :deep(.ms-teams-setting-container) {
    gap: 12px;
  }
  :deep(.ms-teams-settings-card:first-child) {
    gap: 12px;
  }
  :deep(.o-teams-toggle-card .checkbox-content-wrapper) {
    row-gap: 4px;
  }
  :deep(.o-teams-webhook-title) {
    margin-bottom: 0;
  }
  :deep(.o-teams-webhook-input) {
    margin-top: 0;
  }
  // 共通部品の SettingToggleCard は変えず、行の高さをデザイン（16px）に揃える
  :deep(.m-setting-title) {
    line-height: 16px;
  }

  .mail-delivery {
    .c-formBlock__label {
      line-height: 20px;
    }
    .mail-delivery-content-wap {
      .mail-delivery-content {
        .mail-delivery-item {
          .mail-delivery-selectbox {
            > * {
              min-width: 110px;
            }
          }
        }

        &:last-child .spacing-12 {
          display: none;
        }
      }
    }
  }
}
</style>
